/**
 * Self-test suite for the safety-number / fingerprint algorithm.
 *
 * Registered with `SelfTestRunner` so it auto-runs on every `pnpm dev`
 * launch. The primary goal is to guarantee iOS/web parity: every vector
 * pinned here was computed by independently executing the same SHA-256
 * iteration chain described in `Fingerprint.swift`, then cross-checked
 * against a Node.js reference run.
 *
 * See `kaether_for_web/docs/fingerprint.md` for the full algorithm
 * specification and parity guarantee.
 *
 * Cross-platform note: the identical vectors are embedded in
 * `kaether_for_ios/Stoat/E2EE/Fingerprint.swift` as comments starting
 * with "// PARITY VECTOR" so that any future drift between platforms is
 * detectable by running both test suites and comparing.
 *
 * Algorithm origin: iOS `Fingerprint.swift` (canonical).
 *   - SHA-256 (not SHA-512)
 *   - Version prefix [0x00, 0x00] (2 bytes, not 1)
 *   - 5200 total iterations (initial hash + 5199 further rounds)
 *   - Subsequent rounds: SHA-256(prevHash ‖ identityKey)  [no userId]
 *   - 30-byte output split into 6 × 5-byte segments → uint40 % 100_000 → 5 digits
 *   - Two halves joined with a single space; lower userId string comes first
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  computeFingerprintHalf,
  generateFingerprintRaw,
} from "./fingerprint";

// ---------------------------------------------------------------------------
// Known test vectors
// All vectors were computed via the reference Node.js implementation:
//   SHA-256(version[2] ‖ key ‖ userId) then 5199 × SHA-256(hash ‖ key)
// and cross-verified against a manual Swift trace of Fingerprint.swift.
//
// PARITY VECTOR 1: key=0x01×32, userId="alice" / key=0x02×32, userId="bob"
//   alice half : 637058368926492896853434167536
//   bob half   : 397677877833865662775175169480
//   combined   : 637058368926492896853434167536 397677877833865662775175169480
//   (alice < bob lexicographically → alice first)
//
// PARITY VECTOR 2: key=0x01..0x20, userId="user001" / key=0x02..0x40 step 2, userId="user002"
//   half 1 : 607378619597098287892195960037
//   half 2 : 789001029532214546561130226233
//   combined: 607378619597098287892195960037 789001029532214546561130226233
//
// PARITY VECTOR 3: key=0xaa×32, userId="+15551234567" / key=0xbb×32, userId="+15559876543"
//   half A : 666593116987841559788998768063
//   half B : 729447882446739821466840384621
//   combined: 666593116987841559788998768063 729447882446739821466840384621
//   (+15551234567 < +15559876543 lexicographically → A first)
// ---------------------------------------------------------------------------

const VEC1_KEY_A = new Uint8Array(32).fill(0x01);
const VEC1_KEY_B = new Uint8Array(32).fill(0x02);
const VEC1_ID_A = "alice";
const VEC1_ID_B = "bob";
const VEC1_EXPECTED =
  "637058368926492896853434167536 397677877833865662775175169480";

const VEC2_KEY_A = new Uint8Array(
  Array.from({ length: 32 }, (_, i) => i + 1),
);
const VEC2_KEY_B = new Uint8Array(
  Array.from({ length: 32 }, (_, i) => ((i + 1) * 2) % 256),
);
const VEC2_ID_A = "user001";
const VEC2_ID_B = "user002";
const VEC2_EXPECTED =
  "607378619597098287892195960037 789001029532214546561130226233";

const VEC3_KEY_A = new Uint8Array(32).fill(0xaa);
const VEC3_KEY_B = new Uint8Array(32).fill(0xbb);
const VEC3_ID_A = "+15551234567";
const VEC3_ID_B = "+15559876543";
const VEC3_EXPECTED =
  "666593116987841559788998768063 729447882446739821466840384621";

SelfTestRunner.register({
  name: "Safety numbers / fingerprint",
  isVerified: true,

  run: async (): Promise<ReturnType<typeof buildSuiteResult>> => {
    const cases: TestCase[] = [];

    // ------------------------------------------------------------------
    // TC-1: Known vector 1 (all-repeated bytes, short IDs)
    // PARITY VECTOR 1 — also embedded in Fingerprint.swift comments
    // ------------------------------------------------------------------
    try {
      const fp = await generateFingerprintRaw(
        VEC1_KEY_A,
        VEC1_ID_A,
        VEC1_KEY_B,
        VEC1_ID_B,
      );
      if (fp === VEC1_EXPECTED) {
        cases.push(pass("known-vector-1: 0x01/0x02 keys, alice/bob"));
      } else {
        cases.push(
          fail(
            "known-vector-1: 0x01/0x02 keys, alice/bob",
            `expected "${VEC1_EXPECTED}" but got "${fp}"`,
          ),
        );
      }
    } catch (e) {
      cases.push(
        fail(
          "known-vector-1: 0x01/0x02 keys, alice/bob",
          `threw: ${e instanceof Error ? e.message : String(e)}`,
        ),
      );
    }

    // ------------------------------------------------------------------
    // TC-2: Known vector 2 (sequential / doubled bytes, user IDs)
    // PARITY VECTOR 2 — also embedded in Fingerprint.swift comments
    // ------------------------------------------------------------------
    try {
      const fp = await generateFingerprintRaw(
        VEC2_KEY_A,
        VEC2_ID_A,
        VEC2_KEY_B,
        VEC2_ID_B,
      );
      if (fp === VEC2_EXPECTED) {
        cases.push(pass("known-vector-2: sequential/doubled keys, user001/user002"));
      } else {
        cases.push(
          fail(
            "known-vector-2: sequential/doubled keys, user001/user002",
            `expected "${VEC2_EXPECTED}" but got "${fp}"`,
          ),
        );
      }
    } catch (e) {
      cases.push(
        fail(
          "known-vector-2: sequential/doubled keys, user001/user002",
          `threw: ${e instanceof Error ? e.message : String(e)}`,
        ),
      );
    }

    // ------------------------------------------------------------------
    // TC-3: Known vector 3 (0xAA/0xBB bytes, phone-number IDs)
    // PARITY VECTOR 3 — also embedded in Fingerprint.swift comments
    // ------------------------------------------------------------------
    try {
      const fp = await generateFingerprintRaw(
        VEC3_KEY_A,
        VEC3_ID_A,
        VEC3_KEY_B,
        VEC3_ID_B,
      );
      if (fp === VEC3_EXPECTED) {
        cases.push(pass("known-vector-3: 0xAA/0xBB keys, phone-number IDs"));
      } else {
        cases.push(
          fail(
            "known-vector-3: 0xAA/0xBB keys, phone-number IDs",
            `expected "${VEC3_EXPECTED}" but got "${fp}"`,
          ),
        );
      }
    } catch (e) {
      cases.push(
        fail(
          "known-vector-3: 0xAA/0xBB keys, phone-number IDs",
          `threw: ${e instanceof Error ? e.message : String(e)}`,
        ),
      );
    }

    // ------------------------------------------------------------------
    // TC-4: Symmetry — generateFingerprintRaw(A,B) === generateFingerprintRaw(B,A)
    // Both call orderings must produce an identical string because the
    // algorithm sorts halves by userId before concatenation.
    // ------------------------------------------------------------------
    try {
      const [fpAB, fpBA] = await Promise.all([
        generateFingerprintRaw(VEC1_KEY_A, VEC1_ID_A, VEC1_KEY_B, VEC1_ID_B),
        generateFingerprintRaw(VEC1_KEY_B, VEC1_ID_B, VEC1_KEY_A, VEC1_ID_A),
      ]);
      if (fpAB === fpBA) {
        cases.push(pass("symmetry: fingerprint(A,B) === fingerprint(B,A)"));
      } else {
        cases.push(
          fail(
            "symmetry: fingerprint(A,B) === fingerprint(B,A)",
            `AB="${fpAB}" vs BA="${fpBA}"`,
          ),
        );
      }
    } catch (e) {
      cases.push(
        fail(
          "symmetry: fingerprint(A,B) === fingerprint(B,A)",
          `threw: ${e instanceof Error ? e.message : String(e)}`,
        ),
      );
    }

    // ------------------------------------------------------------------
    // TC-5: Avalanche — changing one byte of an identity key changes output
    // ------------------------------------------------------------------
    try {
      const altKey = new Uint8Array(VEC1_KEY_A);
      altKey[0] = 0x02; // flip one byte
      const fpOrig = await generateFingerprintRaw(
        VEC1_KEY_A,
        VEC1_ID_A,
        VEC1_KEY_B,
        VEC1_ID_B,
      );
      const fpAlt = await generateFingerprintRaw(
        altKey,
        VEC1_ID_A,
        VEC1_KEY_B,
        VEC1_ID_B,
      );
      if (fpOrig !== fpAlt) {
        cases.push(pass("avalanche: one-byte identity-key change alters output"));
      } else {
        cases.push(
          fail(
            "avalanche: one-byte identity-key change alters output",
            "fingerprint unchanged after modifying key byte 0",
          ),
        );
      }
    } catch (e) {
      cases.push(
        fail(
          "avalanche: one-byte identity-key change alters output",
          `threw: ${e instanceof Error ? e.message : String(e)}`,
        ),
      );
    }

    // ------------------------------------------------------------------
    // TC-6: Format — output is exactly 61 chars: 30 digits, space, 30 digits
    // ------------------------------------------------------------------
    try {
      const fp = await generateFingerprintRaw(
        VEC1_KEY_A,
        VEC1_ID_A,
        VEC1_KEY_B,
        VEC1_ID_B,
      );
      const formatOk =
        fp.length === 61 &&
        fp[30] === " " &&
        /^[0-9]{30} [0-9]{30}$/.test(fp);
      if (formatOk) {
        cases.push(pass("format: 30 digits, space, 30 digits (61 chars total)"));
      } else {
        cases.push(
          fail(
            "format: 30 digits, space, 30 digits (61 chars total)",
            `bad format: len=${fp.length}, value="${fp}"`,
          ),
        );
      }
    } catch (e) {
      cases.push(
        fail(
          "format: 30 digits, space, 30 digits (61 chars total)",
          `threw: ${e instanceof Error ? e.message : String(e)}`,
        ),
      );
    }

    // ------------------------------------------------------------------
    // TC-7: Wrong-length key rejected with explicit error (not silent garbage)
    // ------------------------------------------------------------------
    {
      const badKey31 = new Uint8Array(31).fill(0x01);
      let threw = false;
      let errorMsg = "";
      try {
        await computeFingerprintHalf(badKey31, "alice");
      } catch (e) {
        threw = true;
        errorMsg = e instanceof Error ? e.message : String(e);
      }
      if (threw && errorMsg.toLowerCase().includes("32")) {
        cases.push(pass("bad-key-length: 31-byte key throws an explicit error"));
      } else if (threw) {
        cases.push(
          fail(
            "bad-key-length: 31-byte key throws an explicit error",
            `threw but message doesn't mention 32: "${errorMsg}"`,
          ),
        );
      } else {
        cases.push(
          fail(
            "bad-key-length: 31-byte key throws an explicit error",
            "no error thrown for 31-byte identity key",
          ),
        );
      }
    }

    // ------------------------------------------------------------------
    // TC-8: Wrong-length key (33 bytes) also rejected
    // ------------------------------------------------------------------
    {
      const badKey33 = new Uint8Array(33).fill(0x01);
      let threw = false;
      try {
        await computeFingerprintHalf(badKey33, "alice");
      } catch {
        threw = true;
      }
      if (threw) {
        cases.push(pass("bad-key-length: 33-byte key throws an explicit error"));
      } else {
        cases.push(
          fail(
            "bad-key-length: 33-byte key throws an explicit error",
            "no error thrown for 33-byte identity key",
          ),
        );
      }
    }

    // ------------------------------------------------------------------
    // TC-9: userId ordering — user ID that sorts higher goes second
    // Verifies that the lower-userId half always occupies the first 30 chars.
    // ------------------------------------------------------------------
    try {
      // "alice" < "bob" so alice's half should be first
      const fp = await generateFingerprintRaw(
        VEC1_KEY_A,
        VEC1_ID_A,   // alice
        VEC1_KEY_B,
        VEC1_ID_B,   // bob
      );
      const aliceHalf = await computeFingerprintHalf(VEC1_KEY_A, VEC1_ID_A);
      const bobHalf = await computeFingerprintHalf(VEC1_KEY_B, VEC1_ID_B);
      const expectedOrder = `${aliceHalf} ${bobHalf}`;
      if (fp === expectedOrder) {
        cases.push(
          pass('userId-ordering: lower userId ("alice" < "bob") appears first'),
        );
      } else {
        cases.push(
          fail(
            'userId-ordering: lower userId ("alice" < "bob") appears first',
            `got "${fp}", expected "${expectedOrder}"`,
          ),
        );
      }
    } catch (e) {
      cases.push(
        fail(
          'userId-ordering: lower userId ("alice" < "bob") appears first',
          `threw: ${e instanceof Error ? e.message : String(e)}`,
        ),
      );
    }

    return buildSuiteResult(cases);
  },
});
